import React, { useState, useEffect, useRef } from 'react';
import { getFirestore, doc, getDoc, setDoc, updateDoc, onSnapshot, deleteField, collection, getDocs, deleteDoc, arrayRemove, increment } from 'firebase/firestore';
import { app, auth, db, API_BASE_URL } from '../../firebase/firebaseConfig';
import { ArrowLeft, Info, Hand, Users, Crown, UserPlus } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback } from '../ui/avatar';
import ErrorModal from '../ui/ErrorModal';

function DeviceSettings({ familyID, onReturn }) {
  const [settings, setSettings] = useState([]);
  const [userValues, setUserValues] = useState({});
  const [categoryMeta, setCategoryMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [autoMode, setAutoMode] = useState(false);
  const [trainingComplete, setTrainingComplete] = useState(true);
  const [hearthSerial, setHearthSerial] = useState(null);
  const [macAddress, setMacAddress] = useState(null);

  // Controllers state
  const [controllers, setControllers] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [isOnlyController, setIsOnlyController] = useState(true);
  const [currentUserAccountUUID, setCurrentUserAccountUUID] = useState(null);
  const [ownerUUIDs, setOwnerUUIDs] = useState([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Real-time listener for familyInfo (autoMode + trainingComplete)
  useEffect(() => {
    const firestore = getFirestore(app);
    const unsub = onSnapshot(doc(firestore, 'familyInfo', familyID), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setAutoMode(data.autoMode || false);
        setTrainingComplete(data.trainingComplete !== false);
      }
    });
    return () => unsub();
  }, [familyID]);

  useEffect(() => {
    window.scrollTo(0, 0);

    const loadSettings = async () => {
      const firestore = getFirestore(app);

      const statusSnap = await getDoc(doc(firestore, 'deviceManagement', familyID, 'status', 'accountVisible'));
      if (!statusSnap.exists()) {
        setLoading(false);
        return;
      }

      const statusData = statusSnap.data();
      const currentVersion = statusData.currentAppVersion;
      const targetVersion = statusData.targetAppVersion;
      const instantUpdate = statusData.instantUpdate ?? false;
      if (statusData.hearthSerial) setHearthSerial(statusData.hearthSerial);
      if (statusData.macAddress) setMacAddress(statusData.macAddress);
      const effectiveVersion = (instantUpdate && targetVersion && targetVersion !== currentVersion)
        ? targetVersion
        : currentVersion;

      if (!effectiveVersion) {
        setLoading(false);
        return;
      }

      const versionDoc = await getDoc(doc(firestore, 'appUpdates', String(effectiveVersion), 'userVisible', 'userSettings'));
      if (!versionDoc.exists()) {
        setLoading(false);
        return;
      }

      const versionData = versionDoc.data();
      const allSettings = [];
      const allUserValues = {};
      const allCategoryMeta = {};

      for (const [category, settingNames] of Object.entries(versionData)) {
        if (!Array.isArray(settingNames) || settingNames.length === 0) continue;

        const categoryDoc = await getDoc(doc(firestore, 'deviceSettings', 'everyAccountAndEveryFamilyVisible', 'settings', category));
        if (!categoryDoc.exists()) continue;
        const categoryData = categoryDoc.data();

        const metaDoc = await getDoc(doc(firestore, 'deviceSettings', 'everyAccountAndEveryFamilyVisible', 'settings', category, 'metadata', 'metadata'));
        if (metaDoc.exists()) {
          allCategoryMeta[category] = metaDoc.data();
        }

        const userSettingsDoc = await getDoc(doc(firestore, 'deviceManagement', familyID, 'userSettings', category));
        const userSettingsData = userSettingsDoc.exists() ? userSettingsDoc.data() : {};

        for (const name of settingNames) {
          if (!categoryData[name]) continue;

          const meta = categoryData[name];
          allSettings.push({ ...meta, name, category });

          if (userSettingsData[name] !== undefined) {
            allUserValues[`${category}/${name}`] = userSettingsData[name];
          }
        }
      }

      allSettings.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setSettings(allSettings);
      setUserValues(allUserValues);
      setCategoryMeta(allCategoryMeta);
      setLoading(false);
    };

    loadSettings();
  }, [familyID]);

  const saveValue = async (category, name, value) => {
    const firestore = getFirestore(app);
    const docRef = doc(firestore, 'deviceManagement', familyID, 'userSettings', category);
    await setDoc(docRef, { [name]: value }, { merge: true });
  };

  const deleteValue = async (category, name) => {
    const firestore = getFirestore(app);
    const docRef = doc(firestore, 'deviceManagement', familyID, 'userSettings', category);
    await setDoc(docRef, { [name]: deleteField() }, { merge: true });
  };

  const handleRepeatTraining = async () => {
    const firestore = getFirestore(app);
    await updateDoc(doc(firestore, 'familyInfo', familyID), { trainingComplete: false });
    setTrainingComplete(false);
  };

  const handleToggleCustom = async (setting, isCustom) => {
    const key = `${setting.category}/${setting.name}`;
    if (isCustom) {
      const defaultVal = setting.value;
      setUserValues(prev => ({ ...prev, [key]: defaultVal }));
      await saveValue(setting.category, setting.name, defaultVal);
    } else {
      setUserValues(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      await deleteValue(setting.category, setting.name);
    }
  };

  const handleValueChange = async (setting, newValue) => {
    const key = `${setting.category}/${setting.name}`;
    setUserValues(prev => ({ ...prev, [key]: newValue }));
    await saveValue(setting.category, setting.name, newValue);
  };

  // Get current user's accountUUID on mount
  useEffect(() => {
    const getAccountUUID = async () => {
      const user = auth.currentUser;
      if (user) {
        const tokenResult = await user.getIdTokenResult();
        setCurrentUserAccountUUID(tokenResult.claims.accountUUID);
      }
    };
    getAccountUUID();
  }, []);

  // Generation counter to discard stale async email-lookup results
  const controllersGenRef = useRef(0);

  // One-time load of owners (rarely changes, no need for live listener)
  useEffect(() => {
    getDocs(collection(db, 'familyInfo', familyID, 'owners'))
      .then(snap => setOwnerUUIDs(snap.docs.map(d => d.id)))
      .catch(err => console.error('Error loading owners:', err));
  }, [familyID]);

  // Live listener on controllers sub-collection
  useEffect(() => {
    const generation = ++controllersGenRef.current;

    const unsub = onSnapshot(
      collection(db, 'familyInfo', familyID, 'controllers'),
      async (snapshot) => {
        const controllersList = [];
        const uuidControllersToLookup = [];

        for (const docSnap of snapshot.docs) {
          const controllerId = docSnap.id;
          if (controllerId.includes('@')) {
            controllersList.push({ controllerId, email: controllerId, isPending: true, isUUIDBased: false });
          } else {
            uuidControllersToLookup.push(controllerId);
            controllersList.push({ controllerId, email: null, isPending: false, isUUIDBased: true });
          }
        }

        if (uuidControllersToLookup.length > 0) {
          const currentUser = auth.currentUser;
          if (currentUser) {
            const idToken = await currentUser.getIdToken();
            const emailResults = await Promise.all(
              uuidControllersToLookup.map(async (uuid) => {
                try {
                  const response = await fetch(`${API_BASE_URL}/user/getEmailForControllerUUID`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
                    body: JSON.stringify({ controllerUUID: uuid }),
                  });
                  if (response.ok) {
                    const data = await response.json();
                    return { uuid, email: data.email };
                  }
                  return { uuid, email: null };
                } catch (error) {
                  console.error(`Error fetching email for ${uuid}:`, error);
                  return { uuid, email: null };
                }
              })
            );
            // Discard if a newer snapshot has already arrived
            if (generation !== controllersGenRef.current) return;
            for (const result of emailResults) {
              const controller = controllersList.find(c => c.controllerId === result.uuid);
              if (controller) controller.email = result.email || result.uuid;
            }
          }
        }

        if (generation !== controllersGenRef.current) return;
        const activeControllers = controllersList.filter(c => !c.isPending);
        setIsOnlyController(activeControllers.length <= 1);
        setControllers(controllersList);
      },
      (error) => console.error('Error listening to controllers:', error)
    );

    return () => unsub();
  }, [familyID]);

  const inviteController = async () => {
    if (!inviteEmail) {
      setErrorMessage('Please enter an email address.');
      setShowErrorModal(true);
      return;
    }
    try {
      await Promise.all([
        setDoc(doc(db, 'familyInfo', familyID, 'controllers', inviteEmail), {}),
        setDoc(doc(db, 'familyInfo', familyID, 'pendingControllerInvites', inviteEmail), {}),
        setDoc(doc(db, 'familyInviteMessages', inviteEmail, 'familyIDs', familyID), {
          type: 'controller',
          timestamp: new Date(),
        }),
      ]);
      setInviteEmail('');
    } catch (error) {
      console.error('Error inviting controller:', error);
    }
  };

  const cancelInvite = async (email) => {
    try {
      await Promise.all([
        deleteDoc(doc(db, 'familyInfo', familyID, 'controllers', email)),
        deleteDoc(doc(db, 'familyInfo', familyID, 'pendingControllerInvites', email)),
        deleteDoc(doc(db, 'familyInviteMessages', email, 'familyIDs', familyID)),
      ]);
    } catch (error) {
      console.error('Error cancelling invite:', error);
      setErrorMessage('Failed to cancel invite. Please try again.');
      setShowErrorModal(true);
    }
  };

  const giveUpControl = async () => {
    const currentUserEmail = auth.currentUser.email;
    try {
      if (!currentUserAccountUUID) {
        setErrorMessage('Unable to determine your account. Please sign in again.');
        setShowErrorModal(true);
        return;
      }
      const accountInfoRef = doc(db, 'accountsInfo', currentUserAccountUUID);
      const accountDoc = await getDoc(accountInfoRef);
      if (accountDoc.exists()) {
        const data = accountDoc.data();
        const ownerDoc = await getDoc(doc(db, 'familyInfo', familyID, 'owners', currentUserAccountUUID));
        if (ownerDoc.exists()) {
          setErrorMessage('You cannot give up control of a Hearth you own.');
          setShowErrorModal(true);
          return;
        }
        if (data.primaryFamilyID === familyID) {
          setErrorMessage('You cannot give up control of your primary Hearth.');
          setShowErrorModal(true);
          return;
        }
        const { updateDoc: firestoreUpdateDoc } = await import('firebase/firestore');
        await firestoreUpdateDoc(accountInfoRef, {
          families: arrayRemove(familyID),
          numOfFamilies: increment(-1),
          numOfInvitedFamilies: increment(-1),
        });
        await Promise.all([
          deleteDoc(doc(db, 'familyInfo', familyID, 'controllers', currentUserAccountUUID)),
          deleteDoc(doc(db, 'familyInfo', familyID, 'pendingControllerInvites', currentUserEmail)).catch(() => {}),
          deleteDoc(doc(db, 'familyInviteMessages', currentUserEmail, 'familyIDs', familyID)).catch(() => {}),
        ]);
        onReturn();
      }
    } catch (error) {
      console.error('Error giving up control:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen w-full flex-col items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-3">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-start gap-4">
          <div className="flex items-start gap-2 flex-1 min-w-0">
            <Button onClick={onReturn} variant="ghost" size="icon" className="flex-shrink-0">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl font-bold text-slate-900">Device Settings</h1>
              <p className="text-slate-600">Customize your Hearth device behavior</p>
            </div>
          </div>
        </div>

        <Card className={autoMode ? 'opacity-50' : ''}>
          <CardHeader className="p-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Hand Control Training</CardTitle>
              {autoMode && (
                <span className="text-xs text-muted-foreground">Not available in Auto Mode</span>
              )}
            </div>
            <p className="text-sm text-muted-foreground">Training helps you understand how to use hand control to navigate your Hearth.</p>
          </CardHeader>
          <CardContent className="p-3">
            <div className={`flex items-center justify-between ${autoMode ? 'pointer-events-none' : ''}`}>
              <span className="font-medium text-sm text-slate-900">Repeat Training</span>
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-1"
                onClick={handleRepeatTraining}
                disabled={autoMode || !trainingComplete}
              >
                <Hand className="h-4 w-4" />
                {!trainingComplete ? 'Pending' : 'Repeat Training'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {settings.length === 0 ? (
          <Card>
            <CardContent className="p-6">
              <p className="text-center text-muted-foreground">No settings available for this device.</p>
            </CardContent>
          </Card>
        ) : (
          (() => {
            const categories = [...new Set(settings.map(s => s.category))];
            return categories.map(cat => {
              const catSettings = settings.filter(s => s.category === cat);
              const meta = categoryMeta[cat];
              const disabledByAutoMode = autoMode && meta?.disabledInAutoMode;
              return (
                <Card key={cat} className={disabledByAutoMode ? 'opacity-50' : ''}>
                  <CardHeader className="p-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">{meta?.label || cat}</CardTitle>
                      {disabledByAutoMode && (
                        <span className="text-xs text-muted-foreground">Not available in Auto Mode</span>
                      )}
                    </div>
                    {meta?.description && (
                      <p className="text-sm text-muted-foreground">{meta.description}</p>
                    )}
                  </CardHeader>
                  <CardContent className={`p-3 ${disabledByAutoMode ? 'pointer-events-none' : ''}`}>
                    {catSettings.map((setting, index) => (
                      <React.Fragment key={`${setting.category}/${setting.name}`}>
                        {index > 0 && <hr className="border-slate-200 my-3" />}
                        <SettingRow
                          setting={setting}
                          userValue={userValues[`${setting.category}/${setting.name}`]}
                          onToggleCustom={handleToggleCustom}
                          onValueChange={handleValueChange}
                          disabled={disabledByAutoMode}
                        />
                      </React.Fragment>
                    ))}
                  </CardContent>
                </Card>
              );
            });
          })()
        )}

        <Card>
          <CardHeader className="p-3">
            <div className="flex items-start gap-2">
              <Users className="h-5 w-5 text-slate-900 flex-shrink-0 mt-0.5" />
              <div>
                <CardTitle className="text-lg leading-none">Controllers</CardTitle>
                <CardDescription className="mt-1">Manage who can control this Hearth</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-3 space-y-3">
            {controllers.map((controller) => {
              const isCurrentUser =
                (controller.isUUIDBased && controller.controllerId === currentUserAccountUUID) ||
                controller.email === auth.currentUser?.email;
              const isOwner = controller.isUUIDBased && ownerUUIDs.includes(controller.controllerId);
              return (
                <div
                  key={controller.controllerId}
                  className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-slate-50 rounded-lg gap-3"
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <Avatar className="h-8 w-8 flex-shrink-0">
                      <AvatarFallback className="text-xs">
                        {controller.email ? controller.email.substring(0, 2).toUpperCase() : '??'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-sm truncate">{controller.email}</span>
                        {controller.isPending ? (
                          <Badge variant="outline" className="text-xs text-slate-500 flex-shrink-0">Pending</Badge>
                        ) : isOwner ? (
                          <Badge variant="secondary" className="flex items-center gap-1 bg-slate-100 text-slate-700 text-xs flex-shrink-0">
                            <Crown className="h-3 w-3" />Owner
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="flex items-center gap-1 text-xs flex-shrink-0">
                            <UserPlus className="h-3 w-3" />Invited
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  {controller.isPending ? (
                    <Button
                      onClick={() => cancelInvite(controller.email)}
                      variant="outline"
                      size="sm"
                      className="w-full sm:w-auto text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      Cancel Invite
                    </Button>
                  ) : isCurrentUser && (
                    <Button
                      onClick={giveUpControl}
                      disabled={isOnlyController}
                      variant="outline"
                      size="sm"
                      className="w-full sm:w-auto"
                    >
                      Give Up Control
                    </Button>
                  )}
                </div>
              );
            })}
            <div className="flex flex-col sm:flex-row gap-2 pt-1">
              <Input
                type="email"
                placeholder="Invite by email address"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && inviteController()}
                className="flex-1"
              />
              <Button
                onClick={inviteController}
                className="flex items-center justify-center gap-2 bg-black text-white w-full sm:w-auto"
              >
                <UserPlus className="h-4 w-4" />
                Invite
              </Button>
            </div>
          </CardContent>
        </Card>

        {macAddress && (
          <p className="text-center text-xs text-slate-400 mt-6">MAC: {macAddress}</p>
        )}
      </div>

      <ErrorModal
        isOpen={showErrorModal}
        message={errorMessage}
        onClose={() => { setShowErrorModal(false); setErrorMessage(''); }}
      />
    </div>
  );
}

function SettingRow({ setting, userValue, onToggleCustom, onValueChange, disabled }) {
  const isDefault = userValue === undefined;
  const displayValue = isDefault ? setting.value : userValue;
  const inputDisabled = isDefault || disabled;

  return (
    <div>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="font-medium text-sm text-slate-900">{setting.label}</span>
            {setting.tooltip && (
              <span className="relative group cursor-help text-slate-400 hover:text-slate-600">
                <Info className="h-3.5 w-3.5" />
                <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1 px-2 py-1 text-xs bg-gray-900 text-white rounded shadow-lg whitespace-normal min-w-[200px] max-w-md hidden group-hover:block z-20">
                  {setting.tooltip}
                </span>
              </span>
            )}
          </div>
          {setting.description && (
            <p className="text-xs text-muted-foreground mt-0.5 mb-0">{setting.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <label className="text-xs text-muted-foreground">Default</label>
          <Switch
            checked={isDefault}
            onCheckedChange={(checked) => onToggleCustom(setting, !checked)}
            disabled={disabled}
          />
        </div>
      </div>
      <div className={`mt-1 ${inputDisabled ? 'opacity-50 pointer-events-none' : ''}`}>
        <SettingInput setting={setting} value={displayValue} onChange={(val) => onValueChange(setting, val)} disabled={inputDisabled} />
      </div>
    </div>
  );
}

function SettingInput({ setting, value, onChange, disabled }) {
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Choices take priority over the type-based widgets: a setting with choices
  // always renders as a dropdown, including bools with labelled choices
  // (e.g. "Version 1"/"Version 2" mapping to false/true).
  if (setting.choices && setting.choices.length > 0) {
    const parseChoiceValue = (val) => {
      // Only bool-typed settings map 'true'/'false' back to booleans — a
      // string-typed setting with literal "true"/"false" choice values must
      // keep them as strings.
      if (setting.type === 'bool') {
        if (val === 'true') return true;
        if (val === 'false') return false;
      }
      return isNaN(Number(val)) ? val : Number(val);
    };
    return (
      <Select value={String(value)} onValueChange={(val) => onChange(parseChoiceValue(val))} disabled={disabled}>
        <SelectTrigger className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {setting.choices.map((choice) => (
            <SelectItem key={String(choice.value)} value={String(choice.value)}>
              {choice.label || String(choice.value)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  if (setting.type === 'bool') {
    return (
      <input
        type="checkbox"
        checked={!!value}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="h-4 w-4 accent-gray-900"
      />
    );
  }

  const isNumerical = setting.type === 'number' || setting.type === 'int' || setting.type === 'float' || typeof setting.value === 'number';
  const min = setting.range?.[0] ?? null;
  const max = setting.range?.[1] ?? null;
  const step = setting.step ?? 1;

  if (isNumerical && min !== null && max !== null) {
    return (
      <div className="flex items-center gap-3">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          disabled={disabled}
          className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-gray-900"
        />
        <span className="text-sm font-medium text-slate-700 w-12 text-right">{value}</span>
      </div>
    );
  }

  return (
    <input
      type="text"
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value)}
      onBlur={() => onChange(localValue)}
      disabled={disabled}
      className="w-full h-8 px-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
    />
  );
}

export default DeviceSettings;
